<template>
  <Teleport to="body">
    <div class="profile-mask" @click.self="handleClose">
      <div class="profile-modal">
        <div class="profile-inner">
          <!-- 头部 -->
          <div class="profile-header">
            <h2 class="profile-title">个人信息</h2>
            <div class="header-actions">
              <button class="back-button" @click="handleClose">
                <img style="width: 14px;height: 14px;" :src="backIcon">
                <span>返回</span>
              </button>
              <button
                class="save-button"
                :disabled="saving || !hasChanges"
                @click="handleSave"
              >
                {{ saving ? '保存中...' : '确认保存' }}
              </button>
            </div>
          </div>

          <!-- 内容区域 -->
          <div class="profile-content">
            <!-- 头像区域 -->
            <div class="avatar-section">
              <div class="avatar-row">
                <img
                  :src="avatarPreview || defaultAvatar"
                  alt="avatar"
                  class="avatar-image"
                />
                <button class="update-avatar-button" @click="handleAvatarClick">
                  更新头像
                </button>
                <button
                  class="delete-avatar-button"
                  @click="handleDeleteAvatar"
                  v-if="avatarPreview && avatarPreview !== defaultAvatar"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M3 4H13M5 4V3C5 2.44772 5.44772 2 6 2H10C10.5523 2 11 2.44772 11 3V4M13 4V13C13 13.5523 12.5523 14 12 14H4C3.44772 14 3 13.5523 3 13V4M6 7V11M10 7V11"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <p class="avatar-hint">支持的格式：JPG、JPEG、PNG。最大尺寸1MB</p>
            </div>

            <!-- 表单区域 -->
            <div class="form-section">
              <div class="form-row">
                <label class="form-field">
                  <span class="field-label">用户名称</span>
                  <div class="input-wrapper">
                    <input
                      v-model.trim="formData.userName"
                      type="text"
                      placeholder="请输入用户名称"
                      class="field-input"
                      @input="handleInputChange"
                    />
                    <img
                      v-if="formData.userName"
                      :src="cleanIcon"
                      alt="清除"
                      class="clean-icon"
                      @click="handleClearUserName"
                    />
                  </div>
                </label>

                <label class="form-field">
                  <span class="field-label">手机号</span>
                  <div class="input-wrapper">
                    <input
                      v-model.trim="formData.phone"
                      type="tel"
                      disabled
                      maxlength="11"
                      placeholder="请输入手机号"
                      class="field-input"
                      :class="{ error: phoneError }"
                      @input="handlePhoneInput"
                    />
                    <img
                      v-if="formData.phone"
                      style="display: none"
                      :src="cleanIcon"
                      alt="清除"
                      class="clean-icon"
                      @click="handleClearPhone"
                    />
                  </div>
                  <span v-if="phoneError" class="error-message">{{ phoneError }}</span>
                </label>
              </div>
            </div>
          </div>

          <!-- 隐藏的文件输入 -->
          <input
            ref="avatarInputRef"
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            style="display: none"
            @change="handleAvatarChange"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, computed, watch } from 'vue';
import defaultAvatar from '@/assets/header_default_avatar.png';
import cleanIcon from '@/assets/clean_icon.png';
import { updateUserInfo, uploadAvatar, SUCCESS_CODE } from '@/api/api';
import { Message } from '@/utils/message';

const props = defineProps({
  userInfo: {
    type: Object,
    default: null
  },
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'update']);
import backIcon from '@/assets/back_icon.png';
const avatarInputRef = ref(null);
const saving = ref(false);
const avatarPreview = ref('');
const avatarFile = ref(null);
const phoneError = ref('');

const formData = ref({
  userName: '',
  phone: ''
});

const originalData = ref({
  userName: '',
  phone: '',
  avatar: ''
});

// 监听 userInfo 变化，初始化表单数据
watch(() => props.userInfo, (newVal) => {
  if (newVal) {
    formData.value = {
      userName: newVal.userName || '',
      phone: newVal.phone || ''
    };
    originalData.value = {
      userName: newVal.userName || '',
      phone: newVal.phone || '',
      avatar: newVal.avatar || ''
    };
    avatarPreview.value = newVal.avatar || '';
  }
}, { immediate: true });

// 监听 visible，重置表单
watch(() => props.visible, (newVal) => {
  if (newVal && props.userInfo) {
    formData.value = {
      userName: props.userInfo.userName || '',
      phone: props.userInfo.phone || ''
    };
    originalData.value = {
      userName: props.userInfo.userName || '',
      phone: props.userInfo.phone || '',
      avatar: props.userInfo.avatar || ''
    };
    avatarPreview.value = props.userInfo.avatar || '';
    avatarFile.value = null;
    phoneError.value = '';
  }
});

// 检查是否有变化
const hasChanges = computed(() => {
  const dataChanged =
    formData.value.userName !== originalData.value.userName ||
    formData.value.phone !== originalData.value.phone;
  const avatarChanged = avatarFile.value !== null;
  return dataChanged || avatarChanged;
});

const handleClose = () => {
  if (saving.value) return;
  emit('close');
};

const handleInputChange = () => {
  // 输入变化时的处理
};

const handlePhoneInput = () => {
  phoneError.value = '';
  handleInputChange();
};

const handleClearUserName = () => {
  formData.value.userName = '';
};

const handleClearPhone = () => {
  formData.value.phone = '';
  phoneError.value = '';
};

const handleAvatarClick = () => {
  avatarInputRef.value?.click();
};

const handleAvatarChange = (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  // 验证文件类型
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!validTypes.includes(file.type)) {
    Message({
      message: '只支持 JPG、JPEG、PNG 格式的图片',
      type: 'error',
      duration: 3000
    });
    return;
  }

  // 验证文件大小 (1MB)
  if (file.size > 1024 * 1024) {
    Message({
      message: '图片大小不能超过 1MB',
      type: 'error',
      duration: 3000
    });
    return;
  }

  // 预览图片
  const reader = new FileReader();
  reader.onload = (e) => {
    avatarPreview.value = e.target.result;
    avatarFile.value = file;
  };
  reader.readAsDataURL(file);
};

const handleDeleteAvatar = () => {
  avatarPreview.value = '';
  // 如果原来有头像，标记为删除
  if (originalData.value.avatar) {
    avatarFile.value = 'DELETE'; // 使用特殊标记表示删除
  } else {
    avatarFile.value = null;
  }
};

const handleSave = async () => {
  if (saving.value || !hasChanges.value) return;

  // 验证手机号
  // if (formData.value.phone && !/^1[3-9]\d{9}$/.test(formData.value.phone)) {
  //   phoneError.value = '手机号格式不正确,请检查';
  //   return;
  // }

  phoneError.value = '';

  saving.value = true;
  try {
    // 先上传头像（如果有）
    let avatarUrl = originalData.value.avatar;
    if (avatarFile.value === 'DELETE') {
      // 删除头像
      avatarUrl = '';
    } else if (avatarFile.value) {
      // 上传新头像
      const uploadRes = await uploadAvatar(avatarFile.value);
      if (uploadRes.code === SUCCESS_CODE) {
        avatarUrl = uploadRes.data?.url || uploadRes.data || avatarUrl;
      } else {
        throw new Error(uploadRes.msg || '头像上传失败');
      }
    }

    // 更新用户信息
    const updateRes = await updateUserInfo(
       formData.value.userName
    );

    if (updateRes.code === SUCCESS_CODE) {
      Message({
        message: '保存成功',
        type: 'success',
        duration: 3000
      });

      // 更新原始数据
      originalData.value = {
        userName: formData.value.userName,
        phone: formData.value.phone,
        avatar: avatarUrl
      };
      avatarPreview.value = avatarUrl;
      avatarFile.value = null;

      // 通知父组件更新
      emit('update', {
        ...formData.value,
        avatar: avatarUrl
      });

      handleClose();
    } else {
      throw new Error(updateRes.msg || '保存失败');
    }
  } catch (error) {
    Message({
      message: error.message || '保存失败，请稍后重试',
      type: 'error',
      duration: 3000
    });
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
.profile-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}

.profile-modal {
  position: relative;
  width: 820px;
  max-width: calc(100% - 48px);
  border-radius: 16px;
  padding: 16px;
  box-sizing: border-box;
  color: #ffffff;
  overflow: visible;
  background:
    radial-gradient(150% 150% at 0% 0%, rgba(250, 216, 124, 0.2) 0%, rgba(250, 216, 124, 0) 60%),
    #1b1813;
  border: 1px solid #fad87c;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10.8px);
}

.profile-inner {
  border-radius: 16px;
  background: rgba(27, 24, 19, 0.7);
  border: 0.95px solid rgba(255, 255, 255, 0.18);
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
}

.profile-title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.36px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #151515;
  background: rgba(71, 71, 71, 0.4);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.back-button:hover {
  background: rgba(71, 71, 71, 0.6);
}

.back-button svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.save-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid #ddb463;
  background: linear-gradient(
    111.27deg,
    rgba(188, 130, 58, 1) 2.48%,
    rgba(247, 207, 115, 1) 39.78%,
    rgba(227, 156, 24, 1) 75%
  );
  color: #12100d;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.save-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-button:not(:disabled):hover {
  opacity: 0.95;
  box-shadow: 0 4px 12px rgba(227, 156, 24, 0.3);
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.avatar-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.avatar-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-image {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  object-fit: cover;
  border: none;
  flex-shrink: 0;
}

.update-avatar-button {
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  background: #4a4136;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.update-avatar-button:hover {
  opacity: 0.9;
}

.delete-avatar-button {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: #4a4136;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.delete-avatar-button:hover {
  opacity: 0.9;
}

.delete-avatar-button svg {
  width: 16px;
  height: 16px;
}

.avatar-hint {
  margin: 0;
  font-size: 12px;
  color: #ffffff;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.form-section {
  display: flex;
  flex-direction: column;
}

.form-row {
  display: flex;
  gap: 16px;
  width: 100%;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.field-label {
  font-size: 16px;
  color: #ffffff;
  font-weight: 400;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.field-input {
  height: 48px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #151515;
  background: rgba(49, 49, 58, 0.4);
  color: #ffffff;
  padding: 0 16px;
  padding-right: 40px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.field-input:focus {
  border-color: #fad87c;
  box-shadow: 0 0 0 1px rgba(250, 216, 124, 0.6);
}

.field-input.error {
  border-color: #ff4444;
}

.field-input.error:focus {
  border-color: #ff4444;
  box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.16);
}

.field-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.clean-icon {
  position: absolute;
  right: 14px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.clean-icon:hover {
  opacity: 1;
}

.error-message {
  font-size: 12px;
  color: #ff4444;
  margin-top: 4px;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}
</style>
